import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { API_URL, AUTH_CALLBACK_ROUTE, GOOGLE_CONNECT_URI } from '@/constants';

/**
 * Native deep-link callback. Must match:
 *  - the URL scheme registered in iOS `Info.plist` (CFBundleURLSchemes) and
 *    Android `AndroidManifest.xml` (intent-filter), and
 *  - a redirect URL accepted by the Strapi Google provider (see NOTE below).
 */
export const NATIVE_AUTH_CALLBACK = 'racore.app://auth/callback';

/**
 * Initiates the Google OAuth flow.
 *
 * Web replacement for the legacy `expo-web-browser` / WebView hybrid flow:
 * we hand the browser off to Strapi's `/connect/google`, which redirects to
 * Google and then back to the app's `/auth/callback` with the tokens in the
 * URL. The callback route (`routes/auth/callback.tsx`) finishes the exchange.
 *
 * Why the explicit `?callback=`:
 * The legacy flow was built for mobile — the native app supplied the `racore://`
 * deep link as the redirect target, and Strapi's grant used it. On web the
 * static store value isn't honored through the cross-site return from Google
 * (grant falls back to `/`, landing on the Strapi admin). So we pass the web
 * callback explicitly: Strapi's connect controller reads `?callback=`, validates
 * it against the stored provider callback (origin + pathname must match), and
 * uses it as grant's redirect. The `racore://` scheme is gone — the redirect
 * target is the HTTPS `/auth/callback` URL of the current origin.
 *
 * NOTE: the Strapi Google provider's "redirect URL to your front-end app" must
 * equal `<origin>/auth/callback` per environment, or the `?callback=` validation
 * (same origin + pathname) will reject it.
 */
export const getGoogleAuthUrl = (): string => {
  if (!API_URL) {
    throw new Error(
      'VITE_API_URL is not set. Create a .env.local with VITE_API_URL=<backend base URL> and restart the dev server.',
    );
  }
  const frontendCallback = `${window.location.origin}${AUTH_CALLBACK_ROUTE}`;
  const callbackParam = encodeURIComponent(frontendCallback);
  return `${API_URL}${GOOGLE_CONNECT_URI}?callback=${callbackParam}`;
};

/**
 * Start the Google OAuth flow.
 *
 * Web: a normal top-level redirect to Strapi's /connect/google.
 *
 * Native (Capacitor): Google BLOCKS OAuth inside embedded WebViews
 * (`disallowed_useragent`), so we must open the flow in the system browser
 * (SFSafariViewController / Chrome Custom Tabs via @capacitor/browser). Strapi
 * then redirects to the app's deep link (`?callback=racore.app://auth/callback`),
 * which the app catches via `@capacitor/app`'s `appUrlOpen` listener
 * (see `useDeepLinkAuth`) to finish the token exchange.
 *
 * NOTE (backend): the Strapi Google provider's "redirect URL to your front-end
 * app" must accept `racore.app://auth/callback` for native, in addition to
 * `<web-origin>/auth/callback` for the browser build.
 */
export const loginWithGoogle = async (): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    // On native the front-end redirect must be the app deep link, not the web
    // origin — override the ?callback with the custom scheme.
    const callbackParam = encodeURIComponent(NATIVE_AUTH_CALLBACK);
    const url = `${API_URL}${GOOGLE_CONNECT_URI}?callback=${callbackParam}`;
    await Browser.open({ url });
    return;
  }
  window.location.assign(getGoogleAuthUrl());
};
